import { type FC } from 'react';

import { Link } from '../Link';
import { Text } from '../Typography';

export type BylineProps = {
  /** Name of the person accountable for the content. */
  authorName: string;
  /** ISO-8601 date or timestamp describing the last meaningful update. */
  lastUpdated: string;
  /** Optional author profile URL. */
  authorHref?: string;
  /** Additional CSS class names. */
  className?: string;
};

const formatDate = (value: string): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    throw new RangeError(`Invalid byline date: ${value}`);
  }

  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
    year: 'numeric',
  }).format(date);
};

const Byline: FC<BylineProps> = ({
  authorName,
  lastUpdated,
  authorHref,
  className,
}) => {
  const author = authorHref ? (
    <Link href={authorHref} underline="none">
      <Text as="span" size="sm">
        {authorName}
      </Text>
    </Link>
  ) : (
    authorName
  );

  return (
    <Text
      className={className}
      color="var(--text-secondary, var(--color-neutral-600))"
      size="sm"
    >
      Last updated <time dateTime={lastUpdated}>{formatDate(lastUpdated)}</time>{' '}
      by {author}
    </Text>
  );
};

export default Byline;
